import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Upload, Download, X, CheckCircle2, AlertCircle, FileSpreadsheet, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * BulkProductUpload — admin modal for uploading an .xlsx / .csv file of
 * products. Talks to:
 *   GET  /api/admin/products/bulk-template  → downloads the .xlsx template
 *   POST /api/admin/products/bulk-upload    → returns { created, updated, failed, errors[], successes[] }
 */
const BulkProductUpload = ({ open, onClose, authHeaders, onUploaded }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const { toast } = useToast();

  if (!open) return null;

  const downloadTemplate = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/admin/products/bulk-template`, {
        headers: authHeaders,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'products-template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      toast({ title: 'Template download failed', description: e.response?.data?.detail || 'Try again' });
    } finally {
      setDownloading(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Max 5 MB.' });
      return;
    }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/products/bulk-upload`, fd, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      toast({
        title: 'Upload complete',
        description: `${res.data.created} created · ${res.data.updated} updated · ${res.data.failed} failed`,
      });
      if (onUploaded) onUploaded(res.data);
    } catch (e) {
      toast({ title: 'Upload failed', description: e.response?.data?.detail || 'Try again' });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const close = () => {
    setResult(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="bulk-upload-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={close} />
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-3xl shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-neutral-200 px-6 py-4 flex items-center justify-between rounded-t-3xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#DEECF9] flex items-center justify-center">
              <FileSpreadsheet className="w-5 h-5 text-[#0078D4]" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-[#0F172A]">Bulk upload products</h3>
              <p className="text-xs text-neutral-500">Excel (.xlsx) or CSV — duplicates are updated by name + category.</p>
            </div>
          </div>
          <button onClick={close} className="p-2 hover:bg-neutral-100 rounded-full" data-testid="bulk-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {!result && (
            <>
              <div className="rounded-2xl border border-dashed border-[#0078D4]/40 bg-[#DEECF9]/30 p-5">
                <div className="text-sm font-semibold text-[#0F172A] mb-1">Step 1 — Download the template</div>
                <p className="text-xs text-neutral-600 mb-3">
                  Required columns are marked with <span className="font-semibold">*</span>. Fill in the rows and save as .xlsx or .csv.
                </p>
                <Button
                  type="button"
                  onClick={downloadTemplate}
                  disabled={downloading}
                  className="h-10 rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white font-semibold"
                  data-testid="download-template-btn"
                >
                  <Download className="w-4 h-4 mr-1.5" />
                  {downloading ? 'Preparing…' : 'Download Excel template'}
                </Button>
              </div>

              <div className="rounded-2xl border border-neutral-200 p-5">
                <div className="text-sm font-semibold text-[#0F172A] mb-1">Step 2 — Upload your file</div>
                <p className="text-xs text-neutral-600 mb-3">
                  Existing products with the same <span className="font-mono">name</span> + <span className="font-mono">category_slug</span> will be updated.
                </p>

                <label
                  className={`flex flex-col items-center justify-center w-full h-40 rounded-2xl border-2 border-dashed cursor-pointer transition ${
                    uploading
                      ? 'bg-slate-50 border-slate-300'
                      : 'bg-slate-50 hover:bg-[#DEECF9]/30 border-slate-300 hover:border-[#0078D4]'
                  }`}
                  data-testid="bulk-upload-dropzone"
                >
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".xlsx,.csv"
                    className="hidden"
                    onChange={(e) => upload(e.target.files?.[0])}
                    disabled={uploading}
                    data-testid="bulk-upload-input"
                  />
                  {uploading ? (
                    <>
                      <Loader2 className="w-8 h-8 text-[#0078D4] animate-spin mb-2" />
                      <span className="text-sm font-medium text-[#0078D4]">Uploading…</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-neutral-400 mb-2" />
                      <span className="text-sm font-medium text-neutral-700">Click to choose .xlsx or .csv</span>
                      <span className="text-xs text-neutral-500 mt-1">or drag & drop · max 5 MB</span>
                    </>
                  )}
                </label>
              </div>
            </>
          )}

          {result && (
            <div className="space-y-4" data-testid="bulk-result">
              <div className="grid grid-cols-3 gap-3">
                <Stat label="Created" value={result.created} color="emerald" testId="bulk-stat-created" />
                <Stat label="Updated" value={result.updated} color="blue" testId="bulk-stat-updated" />
                <Stat label="Failed" value={result.failed} color="red" testId="bulk-stat-failed" />
              </div>

              {result.errors?.length > 0 && (
                <div className="rounded-2xl border border-red-200 bg-red-50/50 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="w-4 h-4 text-red-600" />
                    <div className="text-sm font-semibold text-red-700">Rows that failed</div>
                  </div>
                  <ul className="text-xs text-red-700 space-y-1 max-h-48 overflow-y-auto" data-testid="bulk-error-list">
                    {result.errors.map((e, i) => (
                      <li key={i} className="font-mono">
                        Row {e.row}{e.name ? ` (${e.name})` : ''} — {e.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {result.successes?.length > 0 && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <div className="text-sm font-semibold text-emerald-700">
                      Successfully {result.created + result.updated} row{result.created + result.updated === 1 ? '' : 's'}
                    </div>
                  </div>
                  <ul className="text-xs text-emerald-700 space-y-1 max-h-32 overflow-y-auto">
                    {result.successes.slice(0, 50).map((s, i) => (
                      <li key={i} className="font-mono">
                        Row {s.row} — {s.action} · {s.name}
                      </li>
                    ))}
                    {result.successes.length > 50 && (
                      <li className="italic">…and {result.successes.length - 50} more</li>
                    )}
                  </ul>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-2">
                <Button
                  type="button"
                  onClick={() => setResult(null)}
                  variant="outline"
                  className="h-10 rounded-full"
                  data-testid="bulk-upload-again"
                >
                  Upload another file
                </Button>
                <Button
                  type="button"
                  onClick={close}
                  className="h-10 rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white"
                  data-testid="bulk-done"
                >
                  Done
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value, color, testId }) => {
  const palette = {
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    blue: 'border-[#0078D4]/30 bg-[#DEECF9]/40 text-[#0078D4]',
    red: 'border-red-200 bg-red-50 text-red-700',
  }[color];
  return (
    <div className={`rounded-2xl border p-4 ${palette}`} data-testid={testId}>
      <div className="text-3xl font-extrabold leading-none">{value}</div>
      <div className="text-xs font-semibold uppercase tracking-wide mt-1">{label}</div>
    </div>
  );
};

export default BulkProductUpload;
